import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { DocumentPreviewSliceState, Document } from "./types/redux";

interface HighlightRequest {
  page?: number;
  text?: string;
  [key: string]: unknown;
}

interface DocumentWithHighlight extends Partial<Document> {
  highlightRequest?: HighlightRequest;
}

interface OpenDocumentPayload {
  document: DocumentWithHighlight;
  highlightRequest?: HighlightRequest;
}

// Initial state
const initialState: DocumentPreviewSliceState = {
  openDocuments: [],
  activeDocumentId: null,
  recentDocuments: [],
  isFullScreen: false,
  invalidationCounter: {} as Record<string, number>,
  openSequence: 0,
};

const documentPreviewSlice = createSlice({
  name: 'documentPreview',
  initialState,
  reducers: {
    openDocumentPreview: (state, action: PayloadAction<OpenDocumentPayload | DocumentWithHighlight>) => {
      const payload = action.payload as OpenDocumentPayload;
      const { document, highlightRequest } = payload.document ? payload : { document: payload as unknown as DocumentWithHighlight, highlightRequest: null };

      const documentId = document.docId;
      if (!documentId) {
        console.warn('[DocumentPreview] openDocumentPreview called without docId');
        return;
      }

      const existingIndex = state.openDocuments.findIndex(
        doc => doc.docId === documentId
      );

      if (existingIndex === -1) {
        const docWithHighlight = { ...document };
        if (highlightRequest) {
          docWithHighlight.highlightRequest = highlightRequest;
        }
        state.openDocuments.push(docWithHighlight);
      } else {
        if (highlightRequest) {
          state.openDocuments[existingIndex].highlightRequest = highlightRequest;
        } else {
          delete state.openDocuments[existingIndex].highlightRequest;
        }
      }

      state.activeDocumentId = documentId;

      // Maintain recent documents list
      const recentIndex = state.recentDocuments.findIndex(
        doc => doc.docId === documentId
      );
      if (recentIndex === -1) {
        state.recentDocuments.unshift(document);
        if (state.recentDocuments.length > 10) {
          state.recentDocuments = state.recentDocuments.slice(0, 10);
        }
      } else {
        const existingDoc = state.recentDocuments.splice(recentIndex, 1)[0];
        state.recentDocuments.unshift(existingDoc);
      }

      state.openSequence += 1;
    },

    closeDocumentPreview: (state, action: PayloadAction<string>) => {
      const docId = action.payload;

      state.openDocuments = state.openDocuments.filter(
        doc => doc.docId !== docId
      );

      if (state.activeDocumentId === docId) {
        if (state.openDocuments.length > 0) {
          const lastDoc = state.openDocuments[state.openDocuments.length - 1];
          state.activeDocumentId = lastDoc.docId;
        } else {
          state.activeDocumentId = null;
          state.isFullScreen = false;
        }
      }
    },

    closeAllDocumentPreviews: (state) => {
      state.openDocuments = [];
      state.activeDocumentId = null;
      state.isFullScreen = false;
    },

    setActiveDocument: (state, action: PayloadAction<string>) => {
      const docId = action.payload;
      const document = state.openDocuments.find(doc => doc.docId === docId);
      if (document) {
        state.activeDocumentId = docId;
      }
    },

    toggleFullScreen: (state) => {
      state.isFullScreen = !state.isFullScreen;
    },

    setFullScreen: (state, action: PayloadAction<boolean>) => {
      state.isFullScreen = action.payload;
    },

    clearRecentDocuments: (state) => {
      state.recentDocuments = [];
    },

    removeFromRecent: (state, action: PayloadAction<string>) => {
      const docId = action.payload;
      state.recentDocuments = state.recentDocuments.filter(
        doc => doc.docId !== docId
      );
    },

    setActiveHighlightRequest: (state, action: PayloadAction<HighlightRequest | null>) => {
      const highlightRequest = action.payload;
      const activeId = state.activeDocumentId;

      if (!activeId) return;

      const docIndex = state.openDocuments.findIndex(d => d.docId === activeId);
      if (docIndex !== -1) {
        if (highlightRequest) {
          state.openDocuments[docIndex].highlightRequest = highlightRequest;
        } else {
          delete state.openDocuments[docIndex].highlightRequest;
        }
      }
    },

    invalidateDocumentPreview: (state, action: PayloadAction<string>) => {
      const docId = action.payload;
      const currentCount = state.invalidationCounter[docId] || 0;
      state.invalidationCounter[docId] = currentCount + 1;
    },

    refreshDocumentByName: (state, action: PayloadAction<{ documentName: string; newDocument: DocumentWithHighlight }>) => {
      const { documentName, newDocument } = action.payload;
      const newDocId = newDocument.docId;

      const existingIndex = state.openDocuments.findIndex(
        doc => doc.documentName === documentName
      );

      if (existingIndex !== -1) {
        const oldDocId = state.openDocuments[existingIndex].docId;

        state.openDocuments[existingIndex].docId = newDocId;

        if (state.activeDocumentId === oldDocId) {
          state.activeDocumentId = newDocId;
        }

        const recentIndex = state.recentDocuments.findIndex(
          doc => doc.documentName === documentName
        );
        if (recentIndex !== -1) {
          state.recentDocuments[recentIndex].docId = newDocId;
        }

        const currentCount = state.invalidationCounter[newDocId] || 0;
        state.invalidationCounter[newDocId] = currentCount + 1;
      }
    }
  }
});

export const {
  openDocumentPreview,
  closeDocumentPreview,
  closeAllDocumentPreviews,
  setActiveDocument,
  toggleFullScreen,
  setFullScreen,
  clearRecentDocuments,
  removeFromRecent,
  setActiveHighlightRequest,
  invalidateDocumentPreview,
  refreshDocumentByName
} = documentPreviewSlice.actions;

// Selectors
export const selectOpenDocuments = (state) => state.documentPreview?.openDocuments || [];
export const selectActiveDocumentId = (state) => state.documentPreview?.activeDocumentId;
export const selectActiveDocument = (state) => {
  const openDocs = state.documentPreview?.openDocuments || [];
  const activeId = state.documentPreview?.activeDocumentId;
  return openDocs.find(doc => doc.docId === activeId) || null;
};
export const selectIsPreviewTabVisible = (state) => (state.documentPreview?.openDocuments?.length || 0) > 0;
export const selectIsFullScreen = (state) => state.documentPreview?.isFullScreen || false;
export const selectRecentDocuments = (state) => state.documentPreview?.recentDocuments || [];

export const selectHasDocumentPreview = (state) => {
  return (state.documentPreview?.openDocuments?.length || 0) > 0;
};

export const selectPreviewDocument = (state) => {
  const openDocs = state.documentPreview?.openDocuments || [];
  const activeId = state.documentPreview?.activeDocumentId;
  return openDocs.find(doc => doc.docId === activeId) || null;
};

// Highlight request selectors
export const selectHighlightRequest = (docId) => (state) => {
  const openDocs = state.documentPreview?.openDocuments || [];
  const doc = openDocs.find(d => d.docId === docId);
  return doc?.highlightRequest || null;
};

export const selectActiveHighlightRequest = (state) => {
  const activeId = state.documentPreview?.activeDocumentId;
  if (!activeId) return null;
  const openDocs = state.documentPreview?.openDocuments || [];
  const doc = openDocs.find(d => d.docId === activeId);
  return doc?.highlightRequest || null;
};

export const selectInvalidationCounter = (docId: string) => (state) => {
  return state.documentPreview?.invalidationCounter?.[docId] || 0;
};

export const selectOpenSequence = (state) => state.documentPreview?.openSequence || 0;

export default documentPreviewSlice.reducer;
